import * as React from 'react'
import { ParsedUrlQuery } from 'querystring'
import { useRouter } from 'next/router'
import * as Types from '../../declarations'
import * as DataUtil from '../../lib/data'
import { getContinentURL, getUploadThumbPath } from '../../lib/display'
import { desluggify } from '../../lib/helper'

import ContinentPageTemplate from '../../components/ContinentPageTemplate'

import { withVersion } from '../../lib/withVersion'

interface TrekPageProps {
	trips: Types.DB.Models.TripAttributes[]
	continent: string
	version: Types.VERSION
}

const ContinentPage = ({ trips, continent, version }: TrekPageProps) => {
	const { isFallback } = useRouter()

	if (isFallback) {
		return <div>Loading...</div>
	}

	const uniqueBestMonths = new Set<number>()
	const allUploads: Types.DB.Models.UploadAttributes[] = []
	const locations: { latitude: number; longitude: number }[] = []

	trips.forEach((trip) => {
		const bestMonths = trip.jsonData?.highLevelInfo?.whenToGo?.bestMonths
		if (bestMonths) {
			bestMonths.forEach((month) => uniqueBestMonths.add(month))
		}
		if (trip.uploads) {
			allUploads.push(...trip.uploads)
		}

		const { latitude, longitude } = trip.jsonData.route?.locations?.[0] ?? {}
		if (!!latitude && !!longitude) {
			locations.push({ latitude, longitude })
		}
	})

	const uniqueBestMonthsSorted = Array.from(uniqueBestMonths).sort(
		(a, b) => a - b,
	)

	const canonical = getContinentURL(
		trips?.[0].jsonData.trekOrClimb,
		continent,
		version,
	)

	const shuffleArray = (array: Types.DB.Models.UploadAttributes[]) => {
		for (let i = array.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1))
			;[array[i], array[j]] = [array[j], array[i]] // Swap elements
		}
		return array
	}

	const uploads = shuffleArray(allUploads || []).filter(
		(_, index) => index < 20,
	)

	const categoryDeSlugged = desluggify(continent)

	const seo = {
		title: `${
			version === Types.VERSION.TREKKING
				? 'Trekking trips'
				: 'Mountaineering expeditions'
		} in ${categoryDeSlugged} - Guided ${
			version === Types.VERSION.TREKKING ? 'Trekking' : 'Peaks'
		}`,
		headerTitle: `${
			version === Types.VERSION.TREKKING ? 'Trekking' : 'Mountaineering'
		} in ${categoryDeSlugged}`,
		description: `Research ${
			version === Types.VERSION.TREKKING ? 'trekking' : 'mountaineering'
		} routes in ${categoryDeSlugged}. Whether seeking guided trips or solo adventures, our comprehensive overview has you covered`,
		canonical,
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		images: uploads.map((image) => ({
			url: getUploadThumbPath(image.id, 'large'),
			width: +image.dimensionsLarge.split('x')[0],
			height: +image.dimensionsLarge.split('x')[1],
			alt: continent,
			type: 'image/jpeg',
		})),
	}

	return (
		<>
			<ContinentPageTemplate
				seo={seo}
				version={version}
				category={continent}
				categoryDeSlugged={categoryDeSlugged}
				uniqueBestMonths={uniqueBestMonthsSorted}
				allUploads={uploads}
				trips={trips}
			/>
		</>
	)
}

interface ContinentPageQuery extends ParsedUrlQuery {
	routeType?: string
	continent?: string
}

// for every possible page, generate a static version
// export const getStaticPaths = withVersion(
// 	async (context: { version: Types.VERSION }) => {
// 		const trips = await DataUtil.getHomePageTrips(context.version)

// 		const uniqueContinents = DataUtil.uniqueContinentsFromTrips(trips)

// 		const paths = uniqueContinents.map((continent) => {
// 			// const { trekOrClimb, continent, continents, slug } = trip
// 			return {
// 				params: {
// 					routeType:
// 						context.version === Types.VERSION.TREKKING ? 'trek' : 'climbing',
// 					continent: continent.toLowerCase().replaceAll(' ', '-'),
// 				},
// 			}
// 		})
// 		return { paths, fallback: true }
// 	},
// )

export const getServerSideProps = withVersion(async (context) => {
	const { routeType, continent } = context.params as ContinentPageQuery

	// todo: merge these two 404 checks in to one (route type and slug check), and maybe even the third - no trip found
	if (!(routeType === 'trek' || routeType === 'climbing')) {
		// 404?
		return {
			notFound: true,
		}
	}

	if (!continent) {
		// 404
		return {
			notFound: true,
		}
	}

	const trips = await DataUtil.tripsForContinentPage(context.version, continent)

	return {
		props: {
			trips,
			continent,
			version: context.version,
		},
	}
})

export default ContinentPage
